import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setAuthenticated, setAuth0Token } from "../redux/store/oauthSlice";
import COscarBranding from "./LoginUI/COscarBranding";
import EmailLogin from "./LoginUI/EmailLogin";
import FormStateButtons from "./LoginUI/FormStateButtons";
import { useAuth0 } from "@auth0/auth0-react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Button } from "antd";

const SKIP_AUTH = process.env.REACT_APP_SKIP_AUTH === "true";

export default function RequireAuth({ children }) {
  const [isLoading, setIsLoading] = useState(SKIP_AUTH ? false : true);
  const dispatch = useDispatch();
  const clientId = process.env.REACT_APP_AUTH_CLIENT_ID;
  const formState = useSelector((state) => state.emailLogin.formState);
  const { loginWithRedirect, logout, isAuthenticated: auth0IsAuthenticated, user, getIdTokenClaims, getAccessTokenSilently } = useAuth0();

  // Dev mode: skip Auth0 entirely
  useEffect(() => {
    if (SKIP_AUTH) {
      dispatch(setAuthenticated(true));
      dispatch(setAuth0Token("dev-skip-auth-token"));
    }
  }, [dispatch]);

  useEffect(() => {
    if (SKIP_AUTH) return; // Don't overwrite the dev token

    let mounted = true;

    const setupAuth = async () => {
      if (!mounted) return;

      if (auth0IsAuthenticated) {
        try {
          // Get the ID token
          const idToken = await getIdTokenClaims();
          
          // Check if token is expired
          const expirationTime = idToken.exp * 1000; // Convert to milliseconds
          const currentTime = Date.now();
          
          if (currentTime >= expirationTime) {
            // Token is expired, get a new one
            try {
              console.log('Token expired, refreshing...');
              // Force a complete token refresh cycle
              await getAccessTokenSilently({
                forceRefresh: true,
                cacheMode: 'off'
              });
              // Now get the refreshed ID token
              const newIdToken = await getIdTokenClaims({
                forceRefresh: true
              });
              console.log('New token received:', newIdToken);
              
              if (mounted) {
                if (!newIdToken || !newIdToken.__raw) {
                  console.error('Invalid token received:', newIdToken);
                  throw new Error('Invalid token received');
                }
                dispatch(setAuth0Token(newIdToken.__raw));
                dispatch(setAuthenticated(true));
                console.log('Token refreshed successfully');
              }
            } catch (error) {
              console.error('Error refreshing token:', error);
              if (mounted) {
                dispatch(setAuth0Token(null));
                dispatch(setAuthenticated(false));
                logout({ logoutParams: { returnTo: window.location.origin } });
              }
            }
          } else {
            if (mounted) {
              if (!idToken || !idToken.__raw) {
                console.error('Invalid token:', idToken);
                dispatch(setAuth0Token(null));
                dispatch(setAuthenticated(false));
                logout({ logoutParams: { returnTo: window.location.origin } });
                return;
              }
              dispatch(setAuth0Token(idToken.__raw));
              dispatch(setAuthenticated(true));
            }
          }
        } catch (error) {
          console.error('Error handling authentication:', error);
          if (mounted) {
            dispatch(setAuth0Token(null));
            dispatch(setAuthenticated(false));
            logout({ logoutParams: { returnTo: window.location.origin } });
          }
        }
      } else {
        if (mounted) {
          dispatch(setAuth0Token(null));
          dispatch(setAuthenticated(false));
        }
      }
      if (mounted) {
        setIsLoading(false);
      }
    };

    setupAuth();

    return () => {
      mounted = false;
    };
  }, [auth0IsAuthenticated, getIdTokenClaims, getAccessTokenSilently, logout, dispatch, loginWithRedirect]);

  if (isLoading) {
    return null;
  }

  const getFormTitle = (state) => {
    switch (state) {
      case "login":
        return "Login";
      case "signup":
        return "Sign Up";
      case "forgot":
        return "Reset Password";
      case "reset":
        return "New Password";
      default:
        return "Login";
    }
  };

  // Check if user is authenticated but email is not verified
  if (auth0IsAuthenticated && user && !user.email_verified) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          background: "var(--primary-color)",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <COscarBranding />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "33.33vw",
            minWidth: "523px",
            height: "100%",
            background: "var(--secondary-color)",
            marginLeft: "auto",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <h1>Email Not Verified</h1>
            <p>Please verify your email address to continue.</p>
            <p>Check your inbox for a verification link.</p>
            <Button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              style={{
                marginTop: "20px",
              }}
            >
              Logout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <GoogleOAuthProvider clientId={clientId}>
      {(auth0IsAuthenticated || SKIP_AUTH) ? (
        <>{children}</>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            background: "var(--primary-color)",
            height: "100vh",
            overflow: "auto",
          }}
        >
          <COscarBranding />
          <img
            src="/co_logotype_neon_green.svg"
            alt="Charlie Oscar Logo"
            style={{
              width: "24vw",
              maxHeight: "80vh",
              objectFit: "contain",
              marginLeft: "auto",
              marginTop: "auto",
              paddingRight: "40px",
              paddingBottom: "40px",
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "33.33vw",
              minWidth: "523px",
              minHeight: "100%",
              background: "var(--secondary-color)",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                margin: "auto 0",
              }}
            >
              <h1
                style={{
                  width: "100%",
                  maxWidth: "400px",
                  padding: "0px 24px",
                }}
              >
                {getFormTitle(formState)}
              </h1>
              <EmailLogin />
              <FormStateButtons formState={formState} />
            </div>
            <p
              style={{
                fontSize: "12px",
                padding: "20px 10px",
                textAlign: "center",
                marginBottom: "0px",
              }}
            >
              © Charlie Oscar Group {new Date().getFullYear()}. 22a Leathermarket Street, SE13HP,
              London, United Kingdom
            </p>
          </div>
        </div>
      )}
    </GoogleOAuthProvider>
    </>
  );
}
